import { useCallback, useEffect, useState } from 'react';
import {
  TYPES_CONTRAT,
  annonceRattachable,
  type AnnonceDetectee,
  type AnnonceResume,
  type TypeContrat,
} from '@rh/shared';
import { libelleAnnonceRattachement } from '../annonces-choix';
import { appel } from '../api';
import { dateHeure } from '../format';
import { Erreur } from './Etat';

interface Referentiel {
  id: string;
  libelle: string;
}

interface Reponse {
  annonceId: string;
  reference: string;
  messages: number;
  publicationDeclaree: boolean;
}

/**
 * Annonces pressenties derrière la file « à rattacher »
 * (docs/INGESTION_EMAIL.md § Annonce détectée) : une candidature est arrivée
 * pour une annonce publiée sur une plateforme mais jamais saisie ici. Le
 * responsable RH donne le métier et l'intitulé — l'application ne les devine
 * pas depuis un objet d'e-mail — et tous les messages de la même offre sont
 * rattachés d'un coup.
 */
export function AnnoncesDetectees({ apresCreation }: { apresCreation: () => void }): JSX.Element | null {
  const [groupes, setGroupes] = useState<AnnonceDetectee[] | null>(null);
  const [familles, setFamilles] = useState<Referentiel[]>([]);
  const [annonces, setAnnonces] = useState<AnnonceResume[]>([]);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const charger = useCallback(() => {
    appel<AnnonceDetectee[]>('/ingestion/annonces-detectees')
      .then(setGroupes)
      .catch(() => setGroupes([]));
  }, []);

  useEffect(charger, [charger]);
  useEffect(() => {
    appel<Referentiel[]>('/familles').then(setFamilles).catch(() => setFamilles([]));
    // Un brouillon n'a pas de référence et n'est publié nulle part, une
    // archivée ne reçoit plus rien : ni l'un ni l'autre n'est proposé. Une
    // annonce close, si — la candidature est partie avant la clôture.
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonces(liste.filter((annonce) => annonceRattachable(annonce.statut))))
      .catch(() => setAnnonces([]));
  }, []);

  async function creer(groupe: AnnonceDetectee, saisie: Saisie): Promise<void> {
    setErreur(null);
    setInfo(null);
    try {
      const creee = await appel<Reponse>('/ingestion/annonces-detectees', {
        method: 'POST',
        body: JSON.stringify({
          familleId: saisie.familleId,
          intitule: saisie.intitule.trim(),
          typeContrat: saisie.typeContrat || null,
          plateforme: groupe.plateforme,
          offreExterne: groupe.offreExterne,
          intitulePublie: groupe.intitulePublie,
          messageIds: groupe.messageIds,
        }),
      });
      setOuvert(null);
      setInfo(
        `Annonce ${creee.reference} créée. ${creee.messages} message(s) rattaché(s) : la liste se remplit dans quelques instants.` +
          (creee.publicationDeclaree
            ? ''
            : ' Le numéro d’offre était déjà déclaré ailleurs : les notifications suivantes se rattacheront par l’intitulé.'),
      );
      charger();
      // Le worker rattache en file : la file d'ingestion ne change pas à l'instant.
      setTimeout(apresCreation, 2000);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible');
    }
  }

  /** L'offre était déjà une annonce d'ici, sous un autre intitulé ou republiée. */
  async function rattacher(groupe: AnnonceDetectee, annonceId: string): Promise<void> {
    setErreur(null);
    setInfo(null);
    const annonce = annonces.find((a) => a.id === annonceId);
    try {
      const fait = await appel<{ messages: number }>('/ingestion/annonces-detectees/rattacher', {
        method: 'POST',
        body: JSON.stringify({ annonceId, messageIds: groupe.messageIds }),
      });
      setInfo(
        `${fait.messages} message(s) rattaché(s) à ${annonce?.reference ?? 'l’annonce choisie'} : ` +
          'le traitement se fait dans quelques instants.',
      );
      charger();
      setTimeout(apresCreation, 2000);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Rattachement impossible');
    }
  }

  if (!groupes || groupes.length === 0) {
    return info ? <p className="signal signal-info">{info}</p> : null;
  }

  return (
    <section className="annonces-detectees">
      <h3>
        {groupes.length === 1 ? 'Nouvelle annonce détectée' : `${groupes.length} nouvelles annonces détectées`}
      </h3>
      <p className="mention">
        Des candidatures répondent à une annonce qui n’existe pas dans l’application. Donnez le
        métier et l’intitulé : l’annonce rejoint la liste des annonces en cours, et ses candidatures
        avec elle. Son texte et ses critères restent à compléter.
      </p>
      {info && <p className="signal signal-info">{info}</p>}
      {erreur && <Erreur message={erreur} />}

      <ul className="messages">
        {groupes.map((groupe) => (
          <li key={groupe.cle}>
            <div className="ligne1">
              <strong>{groupe.intitulePublie ?? '(intitulé inconnu)'}</strong>
              <span className="badge">
                {groupe.messages === 1 ? '1 candidature' : `${groupe.messages} candidatures`}
              </span>
            </div>
            <div className="meta">
              <span>{groupe.source}</span>
              {groupe.offreExterne && <span>offre n° {groupe.offreExterne}</span>}
              {groupe.reference && <code>{groupe.reference}</code>}
              {groupe.dernierRecuLe && <span>dernière le {dateHeure(groupe.dernierRecuLe)}</span>}
            </div>
            {groupe.candidats.length > 0 && <p className="synthese">{groupe.candidats.join(' · ')}</p>}

            {ouvert === groupe.cle ? (
              <Formulaire
                groupe={groupe}
                familles={familles}
                onAnnuler={() => setOuvert(null)}
                onCreer={(saisie) => void creer(groupe, saisie)}
              />
            ) : (
              <div className="actions-detectee">
                {/* Deux issues : l'offre est une annonce d'ici qu'on retrouve
                    dans la liste, ou bien elle n'existe pas encore. */}
                <label className="rattachement">
                  Rattacher à
                  <select
                    defaultValue=""
                    onChange={(evenement) => {
                      if (evenement.target.value) {
                        void rattacher(groupe, evenement.target.value);
                        evenement.target.value = '';
                      }
                    }}
                  >
                    <option value="">une annonce existante…</option>
                    {annonces.map((annonce) => (
                      <option key={annonce.id} value={annonce.id}>
                        {libelleAnnonceRattachement(annonce)}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" className="bouton" onClick={() => setOuvert(groupe.cle)}>
                  Créer l’annonce
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

interface Saisie {
  familleId: string;
  intitule: string;
  typeContrat: TypeContrat | '';
}

function Formulaire({
  groupe,
  familles,
  onAnnuler,
  onCreer,
}: {
  groupe: AnnonceDetectee;
  familles: Referentiel[];
  onAnnuler: () => void;
  onCreer: (saisie: Saisie) => void;
}): JSX.Element {
  // L'intitulé publié est une proposition : un objet d'e-mail vaut ce qu'il
  // vaut (« Re: CV »), c'est le responsable RH qui écrit celui de l'annonce.
  const [saisie, setSaisie] = useState<Saisie>({
    familleId: '',
    intitule: groupe.intitulePublie ?? '',
    typeContrat: '',
  });
  const complet = saisie.familleId !== '' && saisie.intitule.trim().length >= 3;

  return (
    <form
      className="saisie"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (complet) onCreer(saisie);
      }}
    >
      <label className="champ">
        Métier
        <select
          value={saisie.familleId}
          onChange={(e) => setSaisie({ ...saisie, familleId: e.target.value })}
        >
          <option value="">Choisir…</option>
          {familles.map((famille) => (
            <option key={famille.id} value={famille.id}>
              {famille.libelle}
            </option>
          ))}
        </select>
        <small className="mention">Il fixe la référence et le barème de notation.</small>
      </label>

      <label className="champ">
        Intitulé de l’annonce
        <input
          value={saisie.intitule}
          maxLength={160}
          onChange={(e) => setSaisie({ ...saisie, intitule: e.target.value })}
        />
      </label>

      <label className="champ">
        Type de contrat
        <select
          value={saisie.typeContrat}
          onChange={(e) => setSaisie({ ...saisie, typeContrat: e.target.value as TypeContrat | '' })}
        >
          <option value="">Non précisé</option>
          {TYPES_CONTRAT.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>

      <div className="actions">
        <button type="submit" className="bouton" disabled={!complet}>
          Créer et rattacher {groupe.messages === 1 ? 'la candidature' : `les ${groupe.messages} candidatures`}
        </button>
        <button type="button" className="lien" onClick={onAnnuler}>
          Annuler
        </button>
      </div>
    </form>
  );
}
